"use client";

import {
    createContext,
    Dispatch,
    FC,
    PropsWithChildren,
    RefObject,
    SetStateAction,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { ICasinoGame, ICasinoSessionResponse } from "@finbackoffice/clientbff-client";
import { useRouter, useSearchParams } from "next/navigation";
import { Currency, Language } from "@finbackoffice/enums";
import { AuthContext, CasinoFavoritesContext, ClientBFFContext } from "@finbackoffice/site-core";
import { CasinoToolsContext, ModalsContextV2, WalletsContext } from "contexts";
import {
    INotificationState,
    useCasinoGames,
    useDevice,
    useFullscreen,
    useNotification,
} from "hooks";
import { RouteNames } from "utils/constants";

type ISessionState = {
    session: ICasinoSessionResponse | null;
    loading: boolean;
};

type ICasinoGameSessionContext = {
    gameId: string;
    sessionState: ISessionState;
    handleGameClose: () => void;
    handleFullScreen: () => void;
    handleGameReload: () => void;
    onFavoriteClickHandler: () => Promise<void>;
    isFavorite: boolean;
    gameData: ICasinoGame | null;
    error: INotificationState;
    isForFun: boolean | null;
    setIsForFun: Dispatch<SetStateAction<boolean | null>>;
    containerRef: RefObject<HTMLIFrameElement | null>;
    isSlots: boolean;
    fullscreen: { document: boolean; css: boolean };
};

export const CasinoGameSessionContext = createContext<ICasinoGameSessionContext>(null as any);

type IProps = {
    isGamePageEnabled: boolean;
    isSlots: boolean;
    language: Language;
    gameId: string;
    homeUrl: string;
};

export const CasinoGameSessionProvider: FC<IProps & PropsWithChildren> = ({
    children,
    isGamePageEnabled,
    isSlots,
    gameId,
    language,
    homeUrl,
}) => {
    const client = useContext(ClientBFFContext);

    const router = useRouter();
    const searchParams = useSearchParams();

    const { isUserLoggedIn } = useContext(AuthContext);
    const { currentWalletId, currentCurrency } = useContext(WalletsContext);
    const { loginModalRef } = useContext(ModalsContextV2);
    const { handleFavoriteClick, isFavorite } = useContext(CasinoFavoritesContext);
    const { previousUrl, showEmptyBalanceError } = useContext(CasinoToolsContext);

    const { isMobileDevice } = useDevice();
    const { notification, setNotification, clearNotification } = useNotification();
    const forFunUrlParam = searchParams?.get("forFun");
    const defaultForFun = forFunUrlParam === "true";

    const [isForFun, setIsForFun] = useState(
        isGamePageEnabled && isSlots ? (forFunUrlParam ? defaultForFun : null) : defaultForFun,
    );
    const { setIsFullscreen, toggleFullscreen, fullscreen } = useFullscreen({
        document: false,
        css: !isGamePageEnabled && isMobileDevice,
    });
    const [sessionState, setSessionState] = useState<ISessionState>({
        session: null,
        loading: false,
    });

    const gameTypesMemo = useMemo(() => [], []);
    const { casinoGamesState } = useCasinoGames(gameTypesMemo, "", "All", {
        gameId: isForFun === null || isGamePageEnabled ? gameId : undefined,
    });

    const containerRef = useRef<HTMLIFrameElement | null>(null);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const isMounted = useRef(false);

    const gameData: ICasinoGame | null = casinoGamesState.games[0] ?? null;

    useEffect(() => {
        if (isForFun === null) {
            setIsFullscreen((state) => ({ ...state, css: false }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isForFun]);

    useEffect(() => {
        if (!isGamePageEnabled && isUserLoggedIn && isForFun === null && forFunUrlParam) {
            setIsForFun(defaultForFun);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentWalletId]);

    useEffect(() => {
        if (isForFun === null) return;

        if (isForFun === false) {
            if (!isUserLoggedIn) {
                setSessionState((state) => ({
                    ...state,
                    session: null,
                }));
                setIsForFun(null);

                if (!loginModalRef.current?.isOpened) {
                    if (timeoutRef.current) {
                        clearTimeout(timeoutRef.current);
                    }

                    timeoutRef.current = setTimeout(() => {
                        loginModalRef.current?.open();
                    }, 500);
                }
            } else if (currentCurrency && (!currentWalletId || currentWalletId < 0)) {
                showEmptyBalanceError();
            } else if (currentCurrency) {
                void loadData();
            }
        } else if (currentCurrency) {
            void loadData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentCurrency, isForFun, currentWalletId]);

    useEffect(() => {
        isMounted.current = true;

        return () => {
            isMounted.current = false;

            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    const navigateToCasinoGamesList = useCallback(() => {
        router.push(homeUrl);
    }, [homeUrl, router]);

    const loadData = useCallback(async () => {
        clearNotification();
        setSessionState((state) => ({
            ...state,
            loading: true,
        }));

        const request = {
            game_id: gameId,
            homeurl: `${window.location.origin}${homeUrl}`,
            language,
        };

        try {
            let response: ICasinoSessionResponse | null = null;
            const session = async () => {
                if (isForFun) {
                    return await client.createCasinoFunSession({
                        ...request,
                        currency: Currency.EUR,
                    });
                } else if (currentWalletId && currentWalletId > 0) {
                    return await client.createCasinoRealSession({
                        ...request,
                        wallet_id: currentWalletId,
                    });
                }
                return null;
            };

            response = await session();

            if (isMounted) {
                if (!response?.iframe_link) {
                    setNotification({
                        type: "error",
                        error: "Error",
                        message: "No iframe link",
                    });
                }

                setSessionState((state) => ({
                    ...state,
                    session: response,
                    loading: false,
                }));
            }
        } catch (err: any) {
            const error = err.response?.data;
            if (isMounted) {
                setNotification({
                    type: "error",
                    error: "Error",
                    message: error.message,
                });

                setSessionState((state) => ({
                    ...state,
                    loading: false,
                }));
            }
        }
    }, [
        clearNotification,
        gameId,
        homeUrl,
        language,
        isForFun,
        currentWalletId,
        client,
        setNotification,
    ]);

    const handleGameClose = useCallback(() => {
        if (previousUrl.includes(RouteNames.CASINO)) {
            router.back();
        } else {
            navigateToCasinoGamesList();
        }
    }, [navigateToCasinoGamesList, previousUrl, router]);

    const handleFullScreen = useCallback(async () => {
        const containerRefCurrent = containerRef.current;

        if (containerRefCurrent) {
            await toggleFullscreen(containerRefCurrent);
        }
    }, [toggleFullscreen]);

    const onFavoriteClickHandler = useCallback(async () => {
        await handleFavoriteClick(gameId);
    }, [gameId, handleFavoriteClick]);

    const handleGameReload = useCallback(() => {
        void loadData();
    }, [loadData]);

    const value = useMemo(
        () => ({
            gameId,
            sessionState,
            handleGameClose,
            handleFullScreen,
            handleGameReload,
            onFavoriteClickHandler,
            isFavorite: isFavorite(gameId),
            gameData,
            error: notification,
            isForFun,
            setIsForFun,
            containerRef,
            isSlots,
            fullscreen,
        }),
        [
            gameId,
            sessionState,
            handleGameClose,
            handleFullScreen,
            handleGameReload,
            onFavoriteClickHandler,
            isFavorite,
            gameData,
            notification,
            isForFun,
            isSlots,
            fullscreen,
        ],
    );

    return (
        <CasinoGameSessionContext.Provider value={value}>
            {children}
        </CasinoGameSessionContext.Provider>
    );
};
